import { getAPI } from "../../../api/Networking";
import {
  OpsAnalyticsData,
  OrdersList,
} from "../../../models/Dashboard/OpsDashboard/OpsAnalytics";
import { OpsAnalyticsAPIFilters } from "../../../models/Dashboard/OpsDashboard/OpsAnalyticsFilters";
import {
  AnalyticsGraphResponse,
  LabelValue,
} from "../../../models/Dashboard/Analytics";
import { PaginatedAPIResponse } from "../../../models/NetworkResponse";
import dayjs from "dayjs";
import { normalizeIds } from "../../../views/components/common/functions/formatDate";

export default class OpsAnalyticsDashboardAPI {
  getReportData = async (url: string, filters: any): Promise<any[]> => {
    try {
      const start_date = dayjs(filters.start_date, "YYYY-MM-DD")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");

      const end_date = dayjs(filters.end_date, "YYYY-MM-DD")
        .add(1, "day")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");

      const queryParams = new URLSearchParams();

      if (start_date) queryParams.append("start_date", start_date);

      if (end_date) queryParams.append("end_date", end_date);

      if (filters.pm_adminids) {
        queryParams.append("pm_adminids", filters.pm_adminids);
      }

      if (filters.order_id) {
        queryParams.append("order_id", filters.order_id);
      }
      if (filters.order_type) {
        queryParams.append("order_type", filters.order_type);
      }
      if (filters.archived) {
        queryParams.append("archived", "true");
      }

      if (filters.vendor_id && filters.vendor_id.length > 0) {
        queryParams.append("vendor_id", filters.vendor_id);
      }

      if (filters.owner_ids && filters.owner_ids?.length > 0) {
        const normalized = normalizeIds(filters.owner_ids);
        if (normalized) {
          queryParams.append("owner_ids", normalized);
        }
      }

      if (filters.excluded_orders) {
        queryParams.append(
          "excluded_orders",
          filters.excluded_orders.toString()
        );
      }

      const response = await getAPI(`${url}?${queryParams.toString()}`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getOpsAnalyticsData = async (
    url: string,
    filters: OpsAnalyticsAPIFilters
  ): Promise<LabelValue[] | AnalyticsGraphResponse[] | OpsAnalyticsData> => {
    try {
      const start_date = dayjs(filters.start_date, "YYYY-MM-DD")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");

      const end_date = dayjs(filters.end_date, "YYYY-MM-DD")
        .add(1, "day")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");

      const queryParams = new URLSearchParams();

      if (start_date) queryParams.append("start_date", start_date);

      if (end_date) queryParams.append("end_date", end_date);

      if (filters.pm_adminids && filters.pm_adminids.length > 0) {
        queryParams.append(
          "pm_adminids",
          filters.pm_adminids.map((item) => item.id).join(",")
        );
      }

      if (filters.order_id && filters.order_id.length > 0) {
        queryParams.append(
          "order_id",
          filters.order_id.map((item) => item.id).join(",")
        );
      }
      if (filters.order_type && filters.order_type.length > 0) {
        queryParams.append(
          "order_type",
          filters.order_type.map((item) => item.id).join(",")
        );
      }
      if (filters.archived && filters.archived.length > 0) {
        queryParams.append("archived", "true");
      }

      if (filters.excluded_orders) {
        queryParams.append(
          "excluded_orders",
          filters.excluded_orders.toString()
        );
      }

      if (filters.excluded_orders) {
        queryParams.append(
          "excluded_orders_from_sourcing",
          filters.excluded_orders.toString()
        );
      }

      const response = await getAPI(
        `/v5/ops-analytics${url}?${queryParams.toString()}`
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getOpsAnalyticsGraphsData = async (
    filters: OpsAnalyticsAPIFilters
  ): Promise<OpsAnalyticsData> => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.start_date)
        queryParams.append("start_date", filters.start_date);

      if (filters.end_date) queryParams.append("end_date", filters.end_date);

      if (filters.pm_adminids && filters.pm_adminids.length > 0) {
        queryParams.append(
          "pm_adminids",
          filters.pm_adminids.map((item) => item.id).join(",")
        );
      }

      if (filters.order_id && filters.order_id.length > 0) {
        queryParams.append(
          "order_id",
          filters.order_id.map((item) => item.id).join(",")
        );
      }
      if (filters.order_type && filters.order_type.length > 0) {
        queryParams.append(
          "order_type",
          filters.order_type.map((item) => item.id).join(",")
        );
      }
      if (filters.archived && filters.archived.length > 0) {
        queryParams.append("archived", "true");
      }

      if (filters.excluded_orders) {
        queryParams.append(
          "excluded_orders",
          filters.excluded_orders.toString()
        );
      }

      const response = await getAPI(
        `/v5/ops-analytics/graphs?${queryParams.toString()}`
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getOrderTableData = async (filters: OpsAnalyticsAPIFilters): Promise<any> => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.page) queryParams.append("page_no", filters.page.toString());
      if (filters.limit) queryParams.append("limit", filters.limit.toString());
      if (filters.sortBy) queryParams.append("sort_by", filters.sortBy);
      if (filters.sortType) queryParams.append("sort_type", filters.sortType);
      if (filters.search) queryParams.append("search_text", filters.search);

      if (filters.start_date)
        queryParams.append("start_date", filters.start_date);

      if (filters.end_date) queryParams.append("end_date", filters.end_date);

      if (filters.pm_adminids && filters.pm_adminids.length > 0) {
        queryParams.append(
          "pm_adminids",
          filters.pm_adminids.map((item) => item.id).join(",")
        );
      }

      if (filters.order_id && filters.order_id.length > 0) {
        queryParams.append(
          "order_id",
          filters.order_id.map((item) => item.id).join(",")
        );
      }
      if (filters.order_type && filters.order_type.length > 0) {
        queryParams.append(
          "order_type",
          filters.order_type.map((item) => item.id).join(",")
        );
      }
      if (filters.archived && filters.archived.length > 0) {
        queryParams.append("archived", "true");
      }

      if (filters.excluded_orders) {
        queryParams.append(
          "excluded_orders",
          filters.excluded_orders.toString()
        );
      }

      const response = await getAPI(
        `/v5/ops-analytics/projects?${queryParams.toString()}`
      );
      return response.result as PaginatedAPIResponse<OrdersList>;
    } catch (error) {
      throw error;
    }
  };
}
